import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "components/ui/Select";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import Modal from "components/ui/modals";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingRow,
  EmptyRow,
} from "components/ui/Table";
import Input from "components/ui/Input";
import useAuth from "hooks/useAuth";
import {
  useReconOrders,
  useCaseDetails,
  useToggleCaseRecon,
  useItemDetails,
  useUpdateItemRecon,
} from "http-hooks/orderrecon/useOrderRecon";

const filterTypeOptions = [
  { value: "N", label: "Non-Reconciled" },
  { value: "A", label: "All Orders" },
];

export default function OrderReconciliation() {
  const [searchParams] = useSearchParams();
  const { agttp } = useAuth();

  const defaultReconType =
    agttp === 0 ? "G" : searchParams.get("recontype") || "P";

  const paramParty = searchParams.get("party");
  const paramPartyCust = searchParams.get("partycust") || paramParty;
  const paramPartyVend = searchParams.get("partyvend") || paramParty;
  const paramOrdersLoaded = searchParams.getAll("ordersloaded");

  const customerInitialValue = useMemo(
    () => (paramPartyCust ? { ps_Id: paramPartyCust } : null),
    [paramPartyCust]
  );
  const vendorInitialValue = useMemo(
    () => (paramPartyVend ? { ps_Id: paramPartyVend } : null),
    [paramPartyVend]
  );

  const [recontype, setRecontype] = useState(defaultReconType);
  const [customer, setCustomer] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [filterType, setFilterType] = useState(filterTypeOptions[0]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [fetchOrders, setFetchOrders] = useState(false);
  const [fetchCases, setFetchCases] = useState(false);

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemModalData, setItemModalData] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});

  const hasAutoFetched = useRef(false);
  const hasAutoSelectedOrders = useRef(false);

  const showCustomer =
    (recontype === "G" && agttp === 0) || (recontype === "S" && agttp === 1);
  const showVendor =
    (recontype === "G" && agttp === 0) || (recontype === "P" && agttp === 1);

  const reconTypeOptions =
    agttp === 1
      ? [
          { value: "P", label: "Purchase (Order Issue)" },
          { value: "S", label: "Sales (Order Receipt)" },
        ]
      : [];

  const orderFilters = {
    recontype,
    filterType: filterType?.value || "N",
    ...(showCustomer && customer ? { customerId: customer.ps_Id } : {}),
    ...(showVendor && vendor ? { vendorId: vendor.ps_Id } : {}),
  };

  const canFetch =
    recontype &&
    (!showCustomer || customer) &&
    (!showVendor || vendor);

  const {
    data: orders,
    isLoading: ordersLoading,
    isFetching: ordersFetching,
  } = useReconOrders(orderFilters, !!(fetchOrders && canFetch));

  const {
    data: caseDetails,
    isLoading: casesLoading,
    isFetching: casesFetching,
  } = useCaseDetails(selectedOrderIds, !!(fetchCases && selectedOrderIds.length > 0));

  const { mutateAsync: mutateToggle, isPending: isToggling } =
    useToggleCaseRecon();

  const { mutateAsync: mutateItemRecon, isPending: isItemSaving } =
    useUpdateItemRecon();

  const itemFilters = itemModalData
    ? {
        orderno: itemModalData.orderno,
        caseno: itemModalData.caseno,
        recontype,
      }
    : {};

  const {
    data: itemDetails,
    isLoading: itemsLoading,
  } = useItemDetails(itemFilters, !!(itemModalOpen && itemModalData));

  useEffect(() => {
    if (itemDetails) {
      const initial = {};
      itemDetails.forEach((item) => {
        initial[item.itemid] = item.itemstat === 0;
      });
      setCheckedItems(initial);
    }
  }, [itemDetails]);

  // Auto-fetch orders when party params are pre-filled via URL
  useEffect(() => {
    if (hasAutoFetched.current) return;
    if (!canFetch) return;
    const hasPartyParam = paramPartyCust || paramPartyVend;
    if (!hasPartyParam) return;
    const partySet =
      (!showCustomer || customer) && (!showVendor || vendor);
    if (partySet) {
      setFetchOrders(true);
      hasAutoFetched.current = true;
    }
  }, [canFetch, customer, vendor, paramPartyCust, paramPartyVend, showCustomer, showVendor]);

  // Auto-select orders from URL param once order list is loaded
  useEffect(() => {
    if (hasAutoSelectedOrders.current) return;
    if (!paramOrdersLoaded || paramOrdersLoaded.length === 0) return;
    if (!orders || orders.length === 0) return;

    const matchingIds = orders
      .filter((o) => paramOrdersLoaded.includes(String(o.orderid)))
      .map((o) => String(o.orderid));

    if (matchingIds.length > 0) {
      setSelectedOrderIds(matchingIds);
      setFetchCases(true);
      hasAutoSelectedOrders.current = true;
    }
  }, [orders, paramOrdersLoaded]);

  const handleUpdate = useCallback(() => {
    setFetchOrders(true);
    setFetchCases(false);
    setSelectedOrderIds([]);
  }, []);

  const handleOrderSelect = useCallback(
    (selectedOptions) => {
      if (!selectedOptions || selectedOptions.length === 0) {
        setSelectedOrderIds([]);
        setFetchCases(false);
        return;
      }
      const ids = selectedOptions.map((opt) =>
        typeof opt === "object" ? opt.value : opt
      );
      setSelectedOrderIds(ids);
      setFetchCases(true);
    },
    []
  );

  const orderOptions = (orders || []).map((o) => ({
    value: String(o.orderid),
    label: o.ordno,
  }));

  const selectedOrderOptions = orderOptions.filter((opt) =>
    selectedOrderIds.includes(opt.value)
  );

  const handleCaseToggle = async (row, checked) => {
    if (!window.confirm("Confirm Y/N")) return;

    try {
      await mutateToggle({
        orderno: row.orderno,
        caseno: row.caseno,
        isChecked: checked,
        recontype,
      });
    } catch (error) {
      console.error("Toggle failed:", error);
    }
  };

  const handleOpenItemModal = (row) => {
    setItemModalData(row);
    setItemModalOpen(true);
  };

  const handleItemCheckChange = (itemid, checked) => {
    setCheckedItems((prev) => ({ ...prev, [itemid]: checked }));
  };

  const handleItemReconSubmit = async () => {
    if (!itemModalData) return;
    const checkedItemIds = Object.keys(checkedItems).filter(
      (id) => checkedItems[id]
    );
    try {
      await mutateItemRecon({
        orderno: itemModalData.orderno,
        caseno: itemModalData.caseno,
        recontype,
        checkedItemIds,
      });
      setItemModalOpen(false);
      setItemModalData(null);
    } catch (error) {
      console.error("Item recon failed:", error);
    }
  };

  const formatNumber = (num, decimals = 2) => {
    return Number(num || 0).toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Order Reconciliation
          </h1>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {agttp === 1 && (
          <Select
            label="Transaction Type"
            options={reconTypeOptions}
            value={reconTypeOptions.find((o) => o.value === recontype) || null}
            onChange={(opt) => {
              setRecontype(opt?.value || "P");
              setCustomer(null);
              setVendor(null);
              setFetchOrders(false);
              setSelectedOrderIds([]);
            }}
            isClearable={false}
          />
        )}

        {showCustomer && (
          <SelectCustomers
            label="Customer"
            value={customer}
            onChange={(val) => setCustomer(val)}
            isRequired={true}
            showAddButton={false}
            initialValue={customerInitialValue}
          />
        )}

        {showVendor && (
          <SelectVendors
            label="Vendor"
            value={vendor}
            onChange={(val) => setVendor(val)}
            isRequired={true}
            showAddButton={false}
            initialValue={vendorInitialValue}
          />
        )}

        <Select
          label="Type"
          options={filterTypeOptions}
          value={filterType}
          onChange={(opt) => setFilterType(opt || filterTypeOptions[0])}
          isClearable={false}
        />
      </div>

      <div className="mb-6">
        <button
          type="button"
          onClick={handleUpdate}
          disabled={!canFetch || ordersFetching}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {ordersFetching ? "Loading..." : "Update"}
        </button>
      </div>

      {/* Main content: split panel */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 border border-gray-200 rounded-lg overflow-hidden"
        style={{ minHeight: "400px", maxHeight: "70vh" }}
      >
        {/* Left panel - Order list */}
        <div className="lg:col-span-4 p-3 border-r border-gray-200 overflow-y-auto">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Order No
          </label>
          {ordersLoading ? (
            <p className="text-sm text-gray-500">Loading orders...</p>
          ) : (
            <Select
              options={orderOptions}
              value={selectedOrderOptions}
              onChange={handleOrderSelect}
              isMulti={true}
              placeholder="Choose orders"
              isSearchable={true}
              isClearable={true}
              showLabel={false}
            />
          )}
        </div>

        {/* Right panel - Case details table */}
        <div className="lg:col-span-8 overflow-auto">
          <Table>
            <THead>
              <TR hover={false}>
                <TH first compact>Order No.</TH>
                <TH compact className="text-center">Case</TH>
                <TH compact className="text-right">Ord Qty</TH>
                <TH compact className="text-right">Lot</TH>
                <TH compact className="text-right">Total</TH>
                <TH compact className="text-right">Bill Qty</TH>
                <TH compact className="text-center w-20" last>
                  Recon
                </TH>
              </TR>
            </THead>
            <TBody>
              {casesLoading || casesFetching ? (
                <LoadingRow colSpan={7} />
              ) : !caseDetails || caseDetails.length === 0 ? (
                <EmptyRow
                  colSpan={7}
                  message={
                    selectedOrderIds.length === 0
                      ? "Select orders to view case details"
                      : "No case details found"
                  }
                />
              ) : (
                caseDetails.map((row, idx) => (
                  <TR key={`${row.orderno}-${row.caseno}-${idx}`}>
                    <TD first compact>
                      <button
                        type="button"
                        className="text-indigo-600 hover:text-indigo-900 font-medium text-sm"
                        onClick={() =>
                          window.open(
                            `/reports/inventory/order-history?orderid=${row.orderid}`,
                            "_blank"
                          )
                        }
                        title="View order history"
                      >
                        {row.orderno}
                      </button>
                    </TD>
                    <TD compact className="text-center">{row.caseno}</TD>
                    <TD compact className="text-right">
                      {formatNumber(row.ordQty)}
                    </TD>
                    <TD compact className="text-right">{row.lotQty}</TD>
                    <TD compact className="text-right font-semibold">
                      {formatNumber(row.total)}
                    </TD>
                    <TD compact className="text-right font-semibold">
                      {formatNumber(row.billQty)}
                    </TD>
                    <TD compact className="text-center" last>
                      <div className="flex items-center justify-center gap-2">
                        <Input
                          type="checkbox"
                          showLabel={false}
                          value={row.casestatus === 0}
                          isDisabled={isToggling}
                          onChange={(e) =>
                            handleCaseToggle(row, e.target.checked)
                          }
                        />
                        <button
                          type="button"
                          className="text-gray-400 hover:text-indigo-600"
                          onClick={() => handleOpenItemModal(row)}
                          title="Item-wise partial closure"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              strokeWidth="2"
                            />
                          </svg>
                        </button>
                      </div>
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </div>
      </div>

      {/* Item-level partial closure modal */}
      <Modal
        open={itemModalOpen}
        setOpen={setItemModalOpen}
        onClose={() => {
          setItemModalData(null);
          setCheckedItems({});
        }}
        maxWidth="2xl"
      >
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            List of Items (Partial Closure)
          </h3>
          {itemModalData && (
            <p className="text-sm text-gray-500 mb-3">
              Order: <span className="font-medium">{itemModalData.orderno}</span>
              {" | "}Case: <span className="font-medium">{itemModalData.caseno}</span>
            </p>
          )}

          <div className="overflow-y-auto" style={{ maxHeight: "350px" }}>
            <Table>
              <THead>
                <TR hover={false}>
                  <TH first compact className="w-[60%]">Item</TH>
                  <TH compact className="text-right w-[15%]">Ord-Qty</TH>
                  <TH compact className="text-right w-[15%]">Bill-Qty</TH>
                  <TH compact className="text-center w-[10%]" last></TH>
                </TR>
              </THead>
              <TBody>
                {itemsLoading ? (
                  <LoadingRow colSpan={4} />
                ) : !itemDetails || itemDetails.length === 0 ? (
                  <EmptyRow colSpan={4} message="No items found" />
                ) : (
                  itemDetails.map((item) => (
                    <TR key={item.itemid}>
                      <TD first compact className="whitespace-normal break-words">
                        {item.itemName}
                      </TD>
                      <TD compact className="text-right">
                        {formatNumber(item.ordQty, 3)}
                      </TD>
                      <TD compact className="text-right">
                        {formatNumber(item.billQty, 3)}
                      </TD>
                      <TD compact className="text-center" last>
                        <Input
                          type="checkbox"
                          showLabel={false}
                          value={!!checkedItems[item.itemid]}
                          onChange={(e) =>
                            handleItemCheckChange(
                              item.itemid,
                              e.target.checked
                            )
                          }
                        />
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </div>

          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={handleItemReconSubmit}
              disabled={isItemSaving}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
            >
              {isItemSaving ? "Saving..." : "Submit"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
